import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDefaultPreferences,
  updateDefaultPreferences,
} from "../services/user";
import { ROLE_CAPABILITIES } from "../config/roleCapabilities";

// SCRUM-992: form state for the Default Account / Location / Landing Page
// dropdowns, shared by User Settings (own defaults) and Edit Staff (pass the
// staff member's staff_info.id). Select values are strings; "" means None.
const EMPTY_FORM = {
  default_account_id: "",
  default_location_id: "",
  default_landing_page: "",
};

const toForm = (data) => ({
  default_account_id: data?.default_account_id ?? "",
  default_location_id:
    data?.default_location_id != null ? String(data.default_location_id) : "",
  default_landing_page: data?.default_landing_page ?? "",
});

// 400 bodies differ by endpoint: the staff endpoint nests single strings under
// `errors`; the profile endpoint puts lists at the top level, like its other
// serializer errors. Either way, keep only the three default_* fields and
// their first message.
const FIELD_KEYS = Object.keys(EMPTY_FORM);
const toFieldErrors = (data) => {
  const source =
    data?.errors && typeof data.errors === "object" ? data.errors : data;
  if (!source || typeof source !== "object") return {};
  return FIELD_KEYS.reduce((acc, key) => {
    const message = Array.isArray(source[key]) ? source[key][0] : source[key];
    if (message) acc[key] = String(message);
    return acc;
  }, {});
};

const isSameForm = (a, b) =>
  a.default_account_id === b.default_account_id &&
  a.default_location_id === b.default_location_id &&
  a.default_landing_page === b.default_landing_page;

export default function useDefaultPreferences({
  staffId = null,
  roleSlug = null,
  enabled = true,
} = {}) {
  const queryClient = useQueryClient();
  // Staff ids are per account, so the staff key includes the selected account.
  const queryKey = staffId
    ? ["default-preferences", "staff", sessionStorage.getItem("acc_id"), staffId]
    : ["default-preferences", "self"];

  const [form, setForm] = useState(EMPTY_FORM);
  const [savedForm, setSavedForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const prefsQuery = useQuery(queryKey, () => getDefaultPreferences(staffId), {
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const prefs = prefsQuery.data;

  useEffect(() => {
    if (!prefs) return;
    const next = toForm(prefs);
    setForm(next);
    setSavedForm(next);
    setErrors({});
  }, [prefs]);

  // The base response already carries location_options for the stored default
  // account; any other account needs its own ?account_id= request.
  const accountId = form.default_account_id;
  const baseHasLocations =
    !!accountId && prefs?.location_options_account_id === accountId;

  const locationsQuery = useQuery(
    [...queryKey, "locations", accountId],
    () => getDefaultPreferences(staffId, accountId),
    {
      enabled: enabled && !!prefs && !!accountId && !baseHasLocations,
      retry: false,
      refetchOnWindowFocus: false,
    }
  );

  let locationOptions = [];
  if (accountId) {
    locationOptions = baseHasLocations
      ? prefs?.location_options || []
      : locationsQuery.data?.location_options || [];
  }

  // The staff endpoint returns the edited user's slug; on User Settings it's
  // the logged-in user's own. my_tasks is already removed by the backend for
  // users without my_tasks_access, which is the same check canAccess() makes
  // at login, so only the other pages are filtered by role here.
  const slug = staffId ? prefs?.role_slug : roleSlug;
  const landingPageOptions = (prefs?.landing_page_options || []).filter(
    (option) =>
      option.id === "my_tasks" || ROLE_CAPABILITIES[slug]?.pages?.[option.id]
  );

  const setAccount = (value) => {
    // Changing the account resets the location. The landing page doesn't
    // depend on which account, so it's kept, but it needs one (AC 5): it's
    // disabled without an account, so going back to None clears it too.
    setForm((prev) => ({
      ...prev,
      default_account_id: value || "",
      default_location_id: "",
      default_landing_page: value ? prev.default_landing_page : "",
    }));
    setErrors((prev) => ({
      ...prev,
      default_account_id: undefined,
      default_location_id: undefined,
    }));
  };

  const setLocation = (value) => {
    setForm((prev) => ({ ...prev, default_location_id: value || "" }));
    setErrors((prev) => ({ ...prev, default_location_id: undefined }));
  };

  const setLandingPage = (value) => {
    setForm((prev) => ({ ...prev, default_landing_page: value || "" }));
    setErrors((prev) => ({ ...prev, default_landing_page: undefined }));
  };

  const isDirty = !isSameForm(form, savedForm);

  // The three default_* keys to send, or {} when nothing changed (so an
  // unchanged form doesn't touch updated_by / updated_at). Account and
  // location are saved as a pair, so all three are sent together.
  const getPayload = () =>
    isDirty
      ? {
          default_account_id: form.default_account_id || null,
          default_location_id: form.default_location_id
            ? Number(form.default_location_id)
            : null,
          default_landing_page: form.default_landing_page || null,
        }
      : {};

  // For hosts that send the defaults in their own request (User Settings
  // folds them into the profile PUT): apply the saved preferences from the
  // response, or the 400 body's field messages.
  const applySaved = (data) => {
    if (data) queryClient.setQueryData(queryKey, data);
  };
  const applyErrors = (data) => setErrors(toFieldErrors(data));

  // Saves the defaults on their own (Edit Staff). Resolves with the saved
  // preferences (or the current ones when nothing changed). Rejects on
  // failure after putting any 400 field messages into `errors`, so the
  // caller can keep its form open and show a toast.
  const save = async () => {
    if (!isDirty) return prefs;
    setErrors({});
    setIsSaving(true);
    try {
      const data = await updateDefaultPreferences(staffId, getPayload());
      applySaved(data);
      return data;
    } catch (error) {
      applyErrors(error?.response?.data);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    // Always shown (hidden only for patients), even while loading or if the
    // defaults can't be loaded; the dropdowns then just offer None.
    isAvailable: enabled && slug !== "patient",
    isLoading: prefsQuery.isLoading,
    loadFailed: prefsQuery.isError,
    prefs,
    form,
    errors,
    isDirty,
    isSaving,
    accountOptions: prefs?.account_options || [],
    locationOptions,
    locationsLoading: !!accountId && !baseHasLocations && locationsQuery.isFetching,
    landingPageOptions,
    setAccount,
    setLocation,
    setLandingPage,
    getPayload,
    applySaved,
    applyErrors,
    save,
  };
}
